/**
 * One page of a business lens's list — the query every server-paged lens list
 * takes. (P2.1: each list used to render the rows the page had loaded — the
 * first 500 — and filter, sort and count them in the browser.)
 *
 * The shared part: the view (active/archived), an optional programme/cohort
 * scope, a search on the talent's name, and the page. Each lens adds its own
 * filters on top.
 */
import { PIPELINE_CARD_TAGS } from '@tonnie/types';
import { z } from 'zod';

const lensPageQuery = {
  view: z.enum(['active', 'archived']).default('active'),
  programId: z.uuid().optional(),
  /** A cohort, or `none` — the talents in no cohort ("Unassigned"). */
  cohortId: z.union([z.uuid(), z.literal('none')]).optional(),
  /** The talent's name contains this. */
  q: z.string().trim().max(200).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(24),
};

/** A Vetting card's status (the server's `vettingStatusOf`). */
export const VETTING_STATUSES = ['completed', 'in_progress', 'pending'] as const;
export type VettingStatusFilter = (typeof VETTING_STATUSES)[number];

/** The badge filter: one of the API's badges, or `none` — no CheckUp score yet. */
export const VETTING_BADGE_FILTERS = ['pro', 'potential', 'noob', 'none'] as const;
export type VettingBadgeFilter = (typeof VETTING_BADGE_FILTERS)[number];

/** `GET /business/vetting/talents` */
export const vettingTalentsQuerySchema = z.object({
  ...lensPageQuery,
  status: z.enum(VETTING_STATUSES).optional(),
  badge: z.enum(VETTING_BADGE_FILTERS).optional(),
});
export type VettingTalentsQuery = z.infer<typeof vettingTalentsQuerySchema>;

/** Whether a value (a URL filter, say) is one of the Vetting statuses. */
export const isVettingStatusFilter = (value: string): value is VettingStatusFilter =>
  (VETTING_STATUSES as readonly string[]).includes(value);

/** Whether a value is one of the Vetting badge filters. */
export const isVettingBadgeFilter = (value: string): value is VettingBadgeFilter =>
  (VETTING_BADGE_FILTERS as readonly string[]).includes(value);

/** A Mobility card's status (the server's `mobilityListFacts`). */
export const MOBILITY_LIST_STATUSES = ['completed', 'on-track', 'blocked', 'at-risk'] as const;
export type MobilityListStatusFilter = (typeof MOBILITY_LIST_STATUSES)[number];
export const MOBILITY_TRACK_FILTERS = ['legal', 'travel', 'setup'] as const;
export type MobilityTrackFilter = (typeof MOBILITY_TRACK_FILTERS)[number];

/** `GET /business/mobility/talents` */
export const mobilityTalentsQuerySchema = z.object({
  ...lensPageQuery,
  track: z.enum(MOBILITY_TRACK_FILTERS).optional(),
  status: z.enum(MOBILITY_LIST_STATUSES).optional(),
});
export type MobilityTalentsQuery = z.infer<typeof mobilityTalentsQuerySchema>;

export const isMobilityListStatusFilter = (value: string): value is MobilityListStatusFilter =>
  (MOBILITY_LIST_STATUSES as readonly string[]).includes(value);
export const isMobilityTrackFilter = (value: string): value is MobilityTrackFilter =>
  (MOBILITY_TRACK_FILTERS as readonly string[]).includes(value);

/**
 * The sponsored talents who have not started a hero — the "Not started yet"
 * card of Mobility, Adaptation and Care. `total` is every one of them; `talents`
 * the first few names the card shows ("and N more" for the rest).
 */
export interface NotStartedList {
  total: number;
  talents: Array<{ id: string; name: string }>;
}
/** How many names the "Not started yet" card shows. */
export const NOT_STARTED_SHOWN = 20;

/**
 * Where a passport sits on the journey bar — the talent list's
 * `stageProgress.current` (`JOURNEY_PROGRESS_STAGES` in @tonnie/types; the
 * server's spec holds the two lists equal).
 */
export const EVIDENCE_STAGE_FILTERS = ['vetting', 'mobility', 'adaptation', 'care'] as const;
export type EvidenceStageFilter = (typeof EVIDENCE_STAGE_FILTERS)[number];

/** `GET /business/evidence/talents` */
export const evidenceTalentsQuerySchema = z.object({
  ...lensPageQuery,
  stage: z.enum(EVIDENCE_STAGE_FILTERS).optional(),
});
export type EvidenceTalentsQuery = z.infer<typeof evidenceTalentsQuerySchema>;

export const isEvidenceStageFilter = (value: string): value is EvidenceStageFilter =>
  (EVIDENCE_STAGE_FILTERS as readonly string[]).includes(value);

/** An Adaptation card's engagement band — `AdaptationEngagementLevel` in @tonnie/heroes. */
export const ADAPTATION_ENGAGEMENT_FILTERS = [
  'thriving',
  'active',
  'slowing',
  'at_risk',
  'inactive',
] as const;
export type AdaptationEngagementFilter = (typeof ADAPTATION_ENGAGEMENT_FILTERS)[number];

/** `GET /business/adaptation/talents` */
export const adaptationTalentsQuerySchema = z.object({
  ...lensPageQuery,
  engagement: z.enum(ADAPTATION_ENGAGEMENT_FILTERS).optional(),
});
export type AdaptationTalentsQuery = z.infer<typeof adaptationTalentsQuerySchema>;

export const isAdaptationEngagementFilter = (value: string): value is AdaptationEngagementFilter =>
  (ADAPTATION_ENGAGEMENT_FILTERS as readonly string[]).includes(value);

/** A Care card's commitment band — `COMMITMENT_BANDS` in @tonnie/heroes (the server spec holds them equal). */
export const CARE_COMMITMENT_FILTERS = [
  'champion',
  'committed',
  'engaged',
  'at_risk',
  'flight_risk',
] as const;
export type CareCommitmentFilter = (typeof CARE_COMMITMENT_FILTERS)[number];

/** A Care pillar — `CANONICAL_CARE_PILLAR_IDS` in @tonnie/heroes (the server spec holds them equal). */
export const CARE_PILLAR_FILTERS = [
  'housing_stability',
  'schooling_childcare',
  'work_fit',
  'wellbeing_social',
] as const;
export type CarePillarFilter = (typeof CARE_PILLAR_FILTERS)[number];

/** The Care list's own filters — a band, and a pillar the talent's latest checkpoint flags as a concern. */
const careListFilters = {
  commitment: z.enum(CARE_COMMITMENT_FILTERS).optional(),
  pillar: z.enum(CARE_PILLAR_FILTERS).optional(),
};

/** `GET /business/care/talents` */
export const careTalentsQuerySchema = z.object({ ...lensPageQuery, ...careListFilters });
export type CareTalentsQuery = z.infer<typeof careTalentsQuerySchema>;

export const isCareCommitmentFilter = (value: string): value is CareCommitmentFilter =>
  (CARE_COMMITMENT_FILTERS as readonly string[]).includes(value);
export const isCarePillarFilter = (value: string): value is CarePillarFilter =>
  (CARE_PILLAR_FILTERS as readonly string[]).includes(value);

/**
 * What a bulk Care intervention reaches (D3): the engagements the company
 * ticked, or every one matching the list's filters — "Select all N". Either
 * way the server reaches only those that need an intervention.
 */
export const careBatchSelectionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('journeys'), journeyIds: z.array(z.uuid()).min(1).max(500) }),
  z.object({
    kind: z.literal('filter'),
    programId: z.uuid().optional(),
    cohortId: lensPageQuery.cohortId,
    q: lensPageQuery.q,
    ...careListFilters,
  }),
]);
export type CareBatchSelection = z.infer<typeof careBatchSelectionSchema>;

/** A card's risk status — the server's `lifecycleStepStatus`. */
export const PIPELINE_STAGE_STATUSES = ['on-track', 'at-risk', 'blocked', 'completed'] as const;
export type PipelineStageStatus = (typeof PIPELINE_STAGE_STATUSES)[number];

/** A comma-separated list of known values — how the board's multi-filters travel. */
const csvOf = <T extends readonly [string, ...string[]]>(values: T) =>
  z
    .string()
    .transform((raw) =>
      raw
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part.length > 0)
    )
    .pipe(z.array(z.enum(values)))
    .optional();

/**
 * `GET /business/pipeline/summary` — the board's KPI row, over the WHOLE
 * pipeline under the board's own filters (D4): the view, a programme, a cohort,
 * the status and tag multi-filters, and the search box.
 */
export const pipelineSummaryQuerySchema = z.object({
  view: z.enum(['active', 'archived']).default('active'),
  programId: z.uuid().optional(),
  cohortId: z.uuid().optional(),
  /** Name, email or role contains this — the board's search box. */
  q: z.string().trim().max(200).optional(),
  status: csvOf(PIPELINE_STAGE_STATUSES),
  tag: csvOf(PIPELINE_CARD_TAGS),
});
export type PipelineSummaryQuery = z.infer<typeof pipelineSummaryQuerySchema>;
